import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { SignaturePad, type SignatureData } from '@/components/ui/SignaturePad';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';

export const PROFILE_TOUR_KEY = 'profile_tour_v1';

type Props = { onDone: () => void };

type IntroStep = { icon: keyof typeof Ionicons.glyphMap; title: string; text: string };

/** Ξενάγηση προφίλ μετά το onboarding: εξήγηση → υποχρεωτική υπογραφή → προαιρετικό κινητό. */
export function ProfileTour({ onDone }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();

  const INTRO: IntroStep[] = [
    {
      icon: 'happy-outline',
      title: t('profileTour.step1_title'),
      text: t('profileTour.step1_text'),
    },
    {
      icon: 'person-circle-outline',
      title: t('profileTour.step2_title'),
      text: t('profileTour.step2_text'),
    },
    {
      icon: 'star-outline',
      title: t('profileTour.step3_title'),
      text: t('profileTour.step3_text'),
    },
  ];
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { save } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [step, setStep] = useState(0);
  const [drawn, setDrawn] = useState<SignatureData | null>(null);
  const [saving, setSaving] = useState(false);

  const sigStep = INTRO.length; // index of signature step
  const phoneStep = sigStep + 1;

  async function finish() {
    try {
      await AsyncStorage.setItem(PROFILE_TOUR_KEY, '1');
    } catch {
      // αγνόησε
    }
    onDone();
  }

  async function saveSignatureAndNext() {
    if (!drawn) return;
    setSaving(true);
    try {
      await save({ signature_svg: JSON.stringify(drawn) });
      setStep(phoneStep);
    } catch {
      // σε αποτυχία, άσε τον χρήστη να ξαναπροσπαθήσει
    } finally {
      setSaving(false);
    }
  }

  return (
    <View style={[styles.overlay, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg }]}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        {step < sigStep ? (
          <View style={styles.card}>
            <View style={styles.iconCircle}>
              <Ionicons name={INTRO[step].icon} size={48} color={colors.primary} />
            </View>
            <Text style={styles.title}>{INTRO[step].title}</Text>
            <Text style={styles.text}>{INTRO[step].text}</Text>
            <Button title={t('common.next')} icon="arrow-forward" onPress={() => setStep(step + 1)} />
            <Text style={styles.progress}>{step + 1} / {phoneStep + 1}</Text>
          </View>
        ) : step === sigStep ? (
          <View style={styles.card}>
            <Text style={styles.title}>{t('profile.tour_signature_title')}</Text>
            <Text style={styles.text}>{t('profileTour.sig_text')}</Text>
            <SignaturePad onChange={setDrawn} height={170} />
            <Button
              title={t('profileTour.save_continue')}
              icon="checkmark"
              loading={saving}
              disabled={!drawn}
              onPress={saveSignatureAndNext}
            />
            <Text style={styles.progress}>{sigStep + 1} / {phoneStep + 1}</Text>
          </View>
        ) : (
          <View style={styles.card}>
            <View style={styles.iconCircle}>
              <Ionicons name="shield-checkmark-outline" size={48} color={colors.primary} />
            </View>
            <Text style={styles.title}>{t('profile.tour_phone_title')}</Text>
            <Text style={styles.text}>{t('profileTour.phone_text')}</Text>
            <Button
              title={t('profileTour.verify_now')}
              icon="phone-portrait-outline"
              onPress={() => {
                void finish();
                router.push('/(app)/settings');
              }}
            />
            <Button title={t('common.skip')} variant="secondary" onPress={() => void finish()} />
            <Text style={styles.progress}>{phoneStep + 1} / {phoneStep + 1}</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.background,
    zIndex: 60,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.lg,
    gap: spacing.md,
  },
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: colors.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  progressWrap: { alignItems: 'center' },
  title: {
    color: colors.text,
    fontSize: fontSize.xl,
    fontWeight: '800',
    textAlign: 'center',
  },
  text: {
    color: colors.textMuted,
    fontSize: fontSize.md,
    lineHeight: 22,
    textAlign: 'center',
  },
  progress: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    textAlign: 'center',
  },
  });
}
